import { dateWords } from '../days.ts';

/**
 * The funnel's time axis (L-21; Jason, 2026-10-08, A: "the axis in quarters while they fit and in years alone once they
 * would crowd; the same thinning keeps a long job that is still running readable"): a tick each quarter (Jan, Apr, Jul,
 * Oct; "Jan 2010" where a year turns, and on the first), and where those would crowd, the years alone ("2010"), every
 * one, then every second, fifth or tenth, the finest that fits. One rule for the drawing across and the one down a phone,
 * each with its own measures.
 */
export interface AxisTick {
  on: string;
  label: string;
}

/** How a drawing lays its axis out: its measure per day, and the least room between two ticks' labels, in the same measure. */
export interface AxisRoom {
  perDay: number;
  /** Between two quarters, where a year's turn writes "Jan 2010". */
  quarters: number;
  /** Between two years written alone, "2010". */
  years: number;
}

/** Every so many years, the finest first. */
const EVERY = [1, 2, 5, 10, 20, 50] as const;

const DAY = 86_400_000;
const at = (y: number, m: number) => new Date(Date.UTC(y + Math.floor(m / 12), m % 12, 1, 12)).toISOString().slice(0, 10);

export function axisTicks(startOn: string, endOn: string, room: AxisRoom): AxisTick[] {
  const t0 = Date.parse(`${startOn}T12:00:00Z`);
  const t1 = Date.parse(`${endOn}T12:00:00Z`);
  if (!(t1 > t0)) return [];
  const start = new Date(t0);
  // Quarters while a quarter's room holds its labels apart: about 91 days between two.
  if (91.3 * room.perDay >= room.quarters) {
    const ticks: AxisTick[] = [];
    for (let m = Math.floor(start.getUTCMonth() / 3) * 3 + 3; ; m += 3) {
      const on = at(start.getUTCFullYear(), m);
      if (Date.parse(`${on}T12:00:00Z`) > t1) break;
      ticks.push({ on, label: dateWords(on, on.slice(5, 7) === '01' || ticks.length === 0 ? 'monthYear' : 'monthOnly') });
    }
    return ticks;
  }
  // The years alone, as often as they fit.
  const every = EVERY.find((n) => 365.25 * n * room.perDay >= room.years) ?? EVERY.at(-1)!;
  const ticks: AxisTick[] = [];
  for (let y = start.getUTCFullYear() + 1; ; y++) {
    const on = at(y, 0);
    if (Date.parse(`${on}T12:00:00Z`) > t1) break;
    if (y % every === 0) ticks.push({ on, label: dateWords(on, 'year') });
  }
  return ticks;
}

/** A drawing's measure per day, across its length for the frame it draws. */
export const perDay = (length: number, startOn: string, endOn: string): number =>
  length / Math.max(1, (Date.parse(`${endOn}T12:00:00Z`) - Date.parse(`${startOn}T12:00:00Z`)) / DAY);
